import React from 'react';
import { Box, Container, Heading, Text, VStack, Badge, HStack, SimpleGrid } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const AIMasteryCurriculum = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Heading size="2xl" fontWeight="900" color="brand.800">
              {data.title}
            </Heading>
            {data.description && (
              <Text fontSize="lg" color="brand.500" maxW="2xl">
                {data.description}
              </Text>
            )}
            {data.subtitle && (
              <Text fontSize="md" color="purple.600" maxW="2xl" fontWeight="600">
                {data.subtitle}
              </Text>
            )}
          </VStack>

          {/* Week by Week Curriculum (New Structure) */}
          {data.weekByWeek && (
            <VStack spacing={10} w="full" align="stretch">
              {data.weekByWeek.map((week, weekIndex) => (
                <MotionBox
                  key={weekIndex}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: weekIndex * 0.1 }}
                >
                  <Box p={8} bg="brand.50" borderRadius="2xl">
                    <VStack spacing={6} align="stretch">
                      <Heading size="lg" color="purple.600">
                        {week.week}
                      </Heading>
                      
                      <VStack spacing={4} align="stretch">
                        {week.sessions?.map((session, sessionIndex) => (
                          <Box
                            key={sessionIndex}
                            p={6}
                            bg="white"
                            borderRadius="xl"
                            borderLeft="6px solid"
                            borderColor={session.badge ? "accent.gold" : "purple.500"}
                            _hover={{
                              transform: 'translateX(8px)',
                              boxShadow: '0 8px 30px rgba(128,90,213,0.15)',
                            }}
                            transition="all 0.3s"
                          >
                            <VStack align="flex-start" spacing={3}>
                              <HStack spacing={3} flexWrap="wrap">
                                <Badge
                                  px={3}
                                  py={1}
                                  bg="purple.600"
                                  color="white"
                                  fontSize="xs"
                                  fontWeight="700"
                                  borderRadius="md"
                                >
                                  Session {session.number}
                                </Badge>
                                {session.badge && (
                                  <Badge
                                    px={3}
                                    py={1}
                                    bg="accent.gold"
                                    color="brand.800"
                                    fontSize="xs"
                                    fontWeight="700"
                                    borderRadius="md"
                                  >
                                    {session.badge}
                                  </Badge>
                                )}
                                <Text fontSize="md" fontWeight="700" color="brand.700">
                                  {session.title}
                                </Text>
                              </HStack>
                              <Text color="brand.600" fontSize="sm" pl={2}>
                                {session.description}
                              </Text>
                              {session.keyTakeaway && (
                                <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                                  Key: {session.keyTakeaway}
                                </Badge>
                              )}
                              {session.visualPlaceholder && (
                                <Box
                                  p={2}
                                  bg="gray.100"
                                  borderRadius="md"
                                  borderStyle="dashed"
                                  borderWidth="1px"
                                  borderColor="gray.300"
                                  w="full"
                                  mt={2}
                                >
                                  <Text color="gray.600" fontSize="xs" fontStyle="italic">
                                    {session.visualPlaceholder}
                                  </Text>
                                </Box>
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </VStack>
          )}

          {/* Bonus Sessions */}
          {data.bonusSessions && (
            <Box w="full" p={6} bg="green.50" borderRadius="2xl">
              <VStack spacing={4} align="stretch">
                <Heading size="md" color="accent.green">
                  Bonus Sessions
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {data.bonusSessions.map((bonus, index) => (
                    <Box key={index} p={4} bg="white" borderRadius="lg">
                      <VStack align="flex-start" spacing={2}>
                        <Text fontWeight="700" color="brand.700">
                          {bonus.title}
                        </Text>
                        <Text fontSize="sm" color="brand.600">
                          {bonus.description}
                        </Text>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </Box>
          )}

          {/* Fallback to old Session Highlights */}
          {data.highlights && !data.weekByWeek && (
            <VStack spacing={6} w="full" align="stretch">
              {data.highlights.map((item, index) => (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Box
                    p={6}
                    bg="brand.50"
                    borderRadius="xl"
                    borderLeft="6px solid"
                    borderColor="purple.500"
                    _hover={{
                      bg: "purple.50",
                      transform: 'translateX(8px)',
                      boxShadow: '0 8px 30px rgba(128,90,213,0.15)',
                    }}
                    transition="all 0.3s"
                  >
                    <VStack align="flex-start" spacing={3}>
                      <HStack>
                        <Badge
                          px={3}
                          py={1}
                          bg="purple.600"
                          color="white"
                          fontSize="xs"
                          fontWeight="700"
                          borderRadius="md"
                        >
                          {item.session}
                        </Badge>
                        <Text fontSize="md" fontWeight="700" color="brand.700">
                          {item.title}
                        </Text>
                      </HStack>
                      <Text color="brand.600" fontSize="sm" pl={2}>
                        {item.highlight}
                      </Text>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </VStack>
          )}

          {/* Visual Placeholder */}
          {data.visualPlaceholder && (
            <Box
              p={6}
              bg="gray.100"
              borderRadius="xl"
              borderStyle="dashed"
              borderWidth="2px"
              borderColor="gray.300"
              textAlign="center"
              w="full"
            >
              <Text color="gray.600" fontSize="sm" fontStyle="italic">
                {data.visualPlaceholder}
              </Text>
            </Box>
          )}

          {/* CTA */}
          
        </VStack>
      </Container>
    </Box>
  );
};

export default AIMasteryCurriculum;
